import { ComboBox } from 'src/components/credit/ui/comboBox/ComboBox';
import DialogModal from 'src/components/ui/dialogModal';
import { IUser } from 'src/utils/interfaces/user';
import { PaymentDataResponse } from 'src/services/models/payment.service';
import { PaymentsService } from 'src/services/payments.service';
import { Permissions } from 'src/utils/enums/permissions.enum';
import Spinner from 'src/components/ui/spinner';
import { Tabs } from '../index';
import TooltipIcon from 'src/components/ui/tooltip';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import {
    AccountType,
    BankAccountType,
    Country,
} from 'src/utils/enums/payment.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FULL_NAME, USER_NAME } from '../../../../../config';
import {
    getBrandByDealerNumber,
    getPermissionsMap,
    isAllow,
} from 'src/utils/helpers';
import { useDispatch, useSelector } from 'react-redux';

interface IProps {
    type: Tabs;
    paymentData: PaymentDataResponse;
    setPaymentInfo: Dispatch<
        SetStateAction<{
            show: boolean;
            type: Tabs;
            paymentData: PaymentDataResponse;
        }>
    >;
    notify: boolean;
    setNotify: Dispatch<boolean>;
    isError: boolean;
    setIsError: Dispatch<boolean>;
    isSavings: Function;
}

const PaymentInformation: React.FC<IProps> = ({
    paymentData,
    type,
    notify,
    setNotify,
    isError,
    setIsError,
    setPaymentInfo,
    isSavings,
}) => {
    const MAX_PAYMENT_AMOUNT = 99999.99;
    const MAX_PAYMENT_AMOUNT_LIMITED = 10000.0;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [country, setCountry] = useState<string>('Canada');
    const [fieldsDisabled, setFieldsDisabled] = useState(false);
    const [showConfimationDialog, setShowConfimationDialog] = useState(false);
    const { payments } = useLocalization();

    const [brand, setBrand] = useState<string>('vw6');

    const [dealerID, setDealerID] = useState('');
    const [person, setPerson] = useState({ name: ' ', email: '' });
    const [dealerName, setDealerName] = useState('');

    const permissionsMap: Map<string, Array<string>> = getPermissionsMap();
    const currentDealers = useSelector((state: IUser) => {
        return state.currentDealers;
    });
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const permissionsToken = useSelector((state: IUser) => {
        return state.permissionsToken;
    });
    const editPaymentInfo = useSelector((state: IUser) => {
        return state.paymentInfo;
    });

    const [payment, setPayment] = useState({
        amount: (editPaymentInfo || {}).PaymentAmount || '0.00',
        reason: (editPaymentInfo || {}).SelectedPaymentReason || '',
        type: isSavings(paymentData.bankAccountDetails.accountType)
            ? payments.PAYMENT_INFORMATION_SAVINGSLABEL
            : payments.PAYMENT_INFORMATION_CHECKINGLABEL,
        dealer: (editPaymentInfo || {}).DealerReferenceId || '',
        comment: (editPaymentInfo || {}).DealerComment || '',
        errAmount: false,
        errReason: false,
        errDealer: false,
        commentErr: false,
    });

    useEffect(() => {
        if (permissionsToken) {
            const fullname = sessionStorage.getItem(FULL_NAME);
            const username = sessionStorage.getItem(USER_NAME);
            if (fullname && username) {
                setPerson({
                    name: fullname,
                    email: username,
                });
            }
        }
    }, []);
    useEffect(() => {
        if (currentDealers && dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            setBrand(getBrandByDealerNumber(dealer.brand, dealer.dealerNumber));
            setDealerID(dealer.dealerNumber);
            setCountry(dealer.country);
            setDealerName(dealer.dealerName);
        }
    }, [dealerDataInfo]);
    useEffect(
        () => () => {
            dispatch({
                type: 'DeletePaymentInfo',
            });
        },
        [],
    );

    const isSubmitForProcessing = isAllow(
        [Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE],
        permissionsMap,
        dealerID,
    );
    const onClickSubmit = () => {
        setLoading(true);
        const amountCheck =
            /^(?!0(\.0{1,2})?$)([1-9]\d{0,2}(,\d{3})*(\.\d{2})?|[1-9]\d{0,5}(\.\d{2})?)$/;
        const maxAmount = isPaymentLimited()
            ? MAX_PAYMENT_AMOUNT_LIMITED
            : MAX_PAYMENT_AMOUNT;
        const numericAmount = Number(
            payment.amount.toString().replace(/,/g, ''),
        );
        if (
            amountCheck.test(payment.amount) === false ||
            numericAmount > maxAmount
        ) {
            setPayment((oldValue) => {
                return { ...oldValue, errAmount: true };
            });
            setLoading(false);
            return;
        } else {
            setPayment((oldValue) => {
                return { ...oldValue, errAmount: false };
            });
        }
        if (payment.reason === '') {
            setPayment((oldValue) => {
                return { ...oldValue, errReason: true };
            });
            setLoading(false);
            return;
        } else {
            setPayment((oldValue) => {
                return { ...oldValue, errReason: false };
            });
        }

        if (payment.dealer.length) {
            const dealerCheck = /^[a-zA-Z0-9]+$/;
            if (dealerCheck.test(payment.dealer) === false) {
                setPayment((oldValue) => {
                    return { ...oldValue, errDealer: true };
                });
                setLoading(false);
                return;
            } else {
                setPayment((oldValue) => {
                    return { ...oldValue, errDealer: false };
                });
            }
        }
        if (
            payment.reason === 'Other' &&
            payment.comment.length === Number('0')
        ) {
            setPayment((oldValue) => {
                return { ...oldValue, commentErr: true };
            });
            setLoading(false);
            return;
        } else if (
            payment.comment.length < Number('5') ||
            payment.comment.length > Number('50')
        ) {
            setPayment((oldValue) => {
                return { ...oldValue, commentErr: true };
            });
            setLoading(false);
            return;
        } else {
            setPayment((oldValue) => {
                return { ...oldValue, commentErr: false };
            });
        }
        isSubmitForProcessing ? setShowConfimationDialog(true) : handleSubmit();
    };

    const handleSubmit = async () => {
        if (showConfimationDialog) setShowConfimationDialog(false);
        const { accountDetail, bankAccountDetails } = paymentData;
        const paymentSave = {
            SelectedPaymentReason: payment.reason,
            AccountNumber: accountDetail.accountNumber,
            Vin: accountDetail.vin,
            AccountType: accountDetail.accountType,
            BankAccount: {
                AccountNumber: bankAccountDetails.accountNumber,
                RoutingNumber: bankAccountDetails.routingNumber,
                ccNumber: bankAccountDetails.ccNumber,
                BankAccountType: isSavings(
                    paymentData.bankAccountDetails.accountType,
                )
                    ? BankAccountType.Saving
                    : BankAccountType.Checking,
            },
            DealerId: dealerID,
            DealerName: dealerName,
            DealerReferenceId: payment.dealer,
            DealerComment: payment.comment,
            Country: country,
            PaymentAmount: +payment.amount,
            TransactionType: type,
            PaymentProcessStatus: isAllow(
                [Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE],
                permissionsMap,
                dealerID,
            )
                ? Number('1')
                : Number('0'),
            UserID: person.email,
            DealerBrand: brand.toLocaleLowerCase().includes('vw')
                ? 'VOLKSWAGEN'
                : brand.toLocaleLowerCase().includes('audi')
                ? 'AUDI'
                : 'DUCATI',
        };

        const service = new PaymentsService();
        try {
            let res;
            if (
                Number(payment.amount) == editPaymentInfo?.PaymentAmount &&
                payment.reason == editPaymentInfo?.SelectedPaymentReason &&
                payment.dealer == editPaymentInfo?.DealerReferenceId &&
                payment.comment == editPaymentInfo?.DealerComment
            ) {
                setFieldsDisabled(true);
                setNotify(true);
                setIsError(false);
                return;
            }
            if (editPaymentInfo) {
                res = await service.paymentEdit({
                    ...paymentSave,
                    PaymentTransactionId: editPaymentInfo.PaymentTransactionId,
                });
            } else {
                res = await service.paymentSave(paymentSave);
            }
            if (res.data && res.data.Payment) {
                setFieldsDisabled(true);
                setNotify(true);
                setIsError(false);
            } else {
                setNotify(true);
                setIsError(true);
            }
            setLoading(false);
        } catch (error) {
            setNotify(true);
            setIsError(true);
            setLoading(false);
        }
    };

    function onCancel() {
        dispatch({
            type: 'DeletePaymentInfo',
        });
        if (editPaymentInfo) {
            navigate('/dashboard/payments/pending');
        }
        setPaymentInfo({
            show: false,
            type: Tabs.PAYMENT,
            paymentData: {
                accountDetail: {
                    accountNumber: '',
                    country: Country.Canada,
                    customerName: '',
                    vehicleMake: '',
                    vehicleModel: '',
                    vehicleYear: '',
                    bodyStyle: '',
                    maturityDate: '',
                    accountType: AccountType.Unknown,
                    accountStatus: '',
                    address: {
                        StateOrProvince: '',
                        TypeCode: '',
                    },
                    prePayLease: false,
                    singlePayLease: false,
                    repossessed: false,
                    vin: '',
                    processStatus: '',
                    TermTypeStatus: '',
                    balloonFinanceType: '',
                    paymentSource: '',
                    contractTerm: 0,
                    partyId: 0,
                },
                privacyFlag: false,
                isBrassHatDealer: false,
                bankAccountDetails: {
                    ccNumber: 0,
                    accountNumber: '',
                    routingNumber: '',
                    accountType: 0,
                },
            },
        });
    }

    const getTitleOptions = () => {
        if (
            paymentData.accountDetail.accountStatus === 'Closed' ||
            paymentData.accountDetail.accountStatus === 'PaidOff'
        ) {
            return [
                {
                    value: 'ChargeBack',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_CHARGEBACK,
                },
            ];
        } else if (paymentData.accountDetail.accountType == 'OperatingLease') {
            return [
                {
                    value: '',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_DEFAULT,
                },
                {
                    value: 'RegularPayment',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_REGULAR,
                },
                {
                    value: 'LeaseTransferFee',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_LEASE,
                },
                {
                    value: 'FlatCancel',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_FLAT,
                },
                {
                    value: 'EndOfTermInvoice',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_INVOICE,
                },
                {
                    value: 'Other',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_OTHER,
                },
            ];
        } else {
            return [
                {
                    value: '',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_DEFAULT,
                },
                {
                    value: 'RegularPayment',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_REGULAR,
                },
                {
                    value: 'Curtailment',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_CURT,
                },
                {
                    value: 'FlatCancel',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_FLAT,
                },
                {
                    value: 'Other',
                    label: payments.PAYMENT_INFORMATION_OPTIONS_OTHER,
                },
            ];
        }
    };
    function isPaymentLimited(): boolean {
        return (
            payment.reason === 'RegularPayment' || payment.reason === 'Other'
        );
    }
    return (
        <>
            <DialogModal
                titleContent={
                    payments.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_TITLE
                }
                bodyContent=''
                open={showConfimationDialog}
                cancelBtnLabel={
                    payments.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_CANCEL
                }
                confirmBtnLabel={
                    payments.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_PROCEED
                }
                onCancelHandler={() => {
                    setShowConfimationDialog(false);
                    setLoading(false);
                }}
                onConfirmHandler={() => {
                    handleSubmit();
                }}
                isContentCentered={false}
                showButtons={true}
                icon={''}
                isBigModal={false}
                isError={false}
                progress={undefined}
                modalExtraClass='payment-approval-confirmation-modal'
            />
            <section className='c-form-section payment-information-section'>
                <div className='c-form-section__content'>
                    <div className='title'>
                        <h6>
                            <span className='c-section-heading__title-text'>
                                {editPaymentInfo
                                    ? payments.PAYMENT_EDIT_INFORMATION_TITLE
                                    : payments.PAYMENT_INFORMATION_TITLE}
                            </span>
                        </h6>
                    </div>
                    <div className='o-fieldset__row u-mt-large u-mb-large'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div>
                                            <label
                                                htmlFor='m1l7e063'
                                                className='c-form-field__label u-mr-xsmall'
                                            >
                                                {
                                                    payments.PAYMENT_INFORMATION_AMOUNTLABEL
                                                }
                                            </label>
                                        </div>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    payment.errAmount
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                onChange={(event) => {
                                                    const re = /^[\d.,]+$/;

                                                    // if value is not blank, then test the regex

                                                    if (
                                                        event.target.value ===
                                                            '' ||
                                                        re.test(
                                                            event.target.value,
                                                        )
                                                    ) {
                                                        setPayment(
                                                            (oldValue) => {
                                                                return {
                                                                    ...oldValue,
                                                                    amount: event
                                                                        .target
                                                                        .value,
                                                                };
                                                            },
                                                        );
                                                    }
                                                }}
                                                type='text'
                                                value={payment.amount}
                                                min={0}
                                                max={
                                                    isPaymentLimited()
                                                        ? MAX_PAYMENT_AMOUNT_LIMITED
                                                        : MAX_PAYMENT_AMOUNT
                                                }
                                                readOnly={fieldsDisabled}
                                            />
                                        </div>
                                        <div>
                                            {payment.errAmount ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {isPaymentLimited()
                                                        ? payments.PAYMENT_INFORMATION_AMOUNT_ERR_LIMIT
                                                        : payments.PAYMENT_INFORMATION_AMOUNTERR}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label u-mr-xsmall'
                                    >
                                        {
                                            payments.PAYMENT_INFORMATION_REASONLABEL
                                        }
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <ComboBox
                                                name={'relationshipSelector'}
                                                options={getTitleOptions()}
                                                defaultValue={''}
                                                placeholder={
                                                    payments.PAYMENT_INFORMATION_OPTIONS_DEFAULT
                                                }
                                                onSelect={(value) => {
                                                    setPayment((oldValue) => {
                                                        return {
                                                            ...oldValue,
                                                            reason: value,
                                                        };
                                                    });
                                                }}
                                                selectedOption={payment.reason}
                                                disabled={fieldsDisabled}
                                            />
                                            {payment.errReason && (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                >
                                                    {
                                                        payments.PAYMENT_INFORMATION_REASONERR
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {payments.PAYMENT_INFORMATION_TYPELABEL}
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <span className='u-mt-xxsmall'>
                                                {payment.type}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row u-mt-large'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div>
                                            <label
                                                htmlFor='m1l7e063'
                                                className='c-form-field__label u-mr-xsmall'
                                            >
                                                {
                                                    payments.PAYMENT_INFORMATION_DEALERREFLABEL
                                                }
                                            </label>
                                        </div>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    payment.errDealer
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                onChange={(event) => {
                                                    const input =
                                                        event.target.value;
                                                    const reg =
                                                        /^[a-zA-Z0-9]*$/;
                                                    if (reg.test(input)) {
                                                        setPayment((prev) => ({
                                                            ...prev,
                                                            dealer: input.toUpperCase(),
                                                        }));
                                                    }
                                                }}
                                                type='text'
                                                value={payment.dealer}
                                                maxLength={10}
                                                readOnly={fieldsDisabled}
                                            />
                                        </div>
                                        <div>
                                            {payment.errDealer ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {
                                                        payments.PAYMENT_INFORMATION_DEALERREFERR
                                                    }
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div>
                                            <label
                                                htmlFor='m1l7e063'
                                                className='c-form-field__label u-mr-xsmall'
                                            >
                                                {
                                                    payments.PAYMENT_INFORMATION_COMMENTLABEL
                                                }
                                            </label>
                                            <TooltipIcon
                                                text={
                                                    payments.PAYMENT_INFORMATION_COMMENTIP
                                                }
                                            ></TooltipIcon>
                                        </div>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    payment.commentErr
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                onChange={(event) =>
                                                    setPayment((oldValue) => {
                                                        return {
                                                            ...oldValue,
                                                            comment:
                                                                event.target
                                                                    .value,
                                                        };
                                                    })
                                                }
                                                type='text'
                                                value={payment.comment}
                                                minLength={5}
                                                maxLength={50}
                                                readOnly={fieldsDisabled}
                                            />
                                        </div>
                                        <div>
                                            {payment.commentErr ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    <>
                                                        {payment.reason ===
                                                            'Other' &&
                                                        payment.comment
                                                            .length ===
                                                            Number('0') &&
                                                        payment.commentErr ? (
                                                            <div>
                                                                {
                                                                    payments.PAYMENT_INFORMATION_COMMENT1ERR
                                                                }
                                                                <br />
                                                            </div>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </>
                                                    <>
                                                        {payment.comment
                                                            .length <
                                                            Number('5') ||
                                                        payment.commentErr
                                                            ? payments.PAYMENT_INFORMATION_COMMENT2ERR
                                                            : ''}
                                                    </>
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row u-mt-large'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/1  u-1/1@xs'>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: payments.PAYMENT_INFORMATION_NOTE,
                                    }}
                                ></span>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row u-mt-large'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/1  u-1/1@xs'>
                                <div className='o-fieldset__row button-group'>
                                    <button
                                        className={
                                            'c-btn c-btn--secondary u-mr-small'
                                        }
                                        type='button'
                                        onClick={onCancel}
                                    >
                                        <span className='c-btn__text'>
                                            {notify && !isError
                                                ? payments.PAYMENT_INFORMATION_RETURN_LABEL
                                                : payments.PAYMENT_INFORMATION_CANCELBTN}
                                        </span>
                                    </button>
                                    {notify && !isError ? null : (
                                        <button
                                            className={'c-btn'}
                                            type='button'
                                            onClick={onClickSubmit}
                                            disabled={
                                                !(
                                                    ('' + payment.amount)
                                                        .length &&
                                                    payment.reason.length &&
                                                    payment.comment.length
                                                ) || loading
                                            }
                                        >
                                            <span className='c-btn__text'>
                                                {loading ? (
                                                    <Spinner />
                                                ) : isAllow(
                                                      [
                                                          Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
                                                      ],
                                                      permissionsMap,
                                                      dealerID,
                                                  ) ? (
                                                    payments.PAYMENT_INFORMATION_SUBMITBTN
                                                ) : (
                                                    payments.PAYMENT_INFORMATION_SUBMITAPPBTN
                                                )}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PaymentInformation;
